import axios from 'axios'
import { auth } from '@/config/api'

// Create axios instance for email authentication
const emailAuthClient = axios.create({
  baseURL: auth,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true
})

/**
 * Check if email exists and send OTP for login/register
 */
export async function checkEmail(email, recaptchaVerificationId = null) {
  try {
    const payload = { email }
    if (recaptchaVerificationId) {
      payload.recaptchaVerificationId = recaptchaVerificationId
    }

    const response = await emailAuthClient.post('/email/check', payload)
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to check email')
  }
}

/**
 * Register user with name (step before OTP)
 */
export async function registerUser(email, name) {
  try {
    const response = await emailAuthClient.post('/email/register-user', { email, name })
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to register user')
  }
}

/**
 * Login with email, OTP, and password
 */
export async function loginWithEmail(email, otp, password) {
  try {
    const response = await emailAuthClient.post('/email/login', { email, otp, password })
    
    if (response.data.success && response.data.token) {
      // Store the token
      localStorage.setItem('auth_token', response.data.token)
    }
    
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to login')
  }
}

/**
 * Register with email, name, OTP, and password
 */
export async function registerWithEmail(email, name, otp, password) {
  try {
    const response = await emailAuthClient.post('/email/register', { email, name, otp, password })
    
    if (response.data.success && response.data.token) {
      // Store the token
      localStorage.setItem('auth_token', response.data.token)
    }
    
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to register')
  }
}

/**
 * Validate email format
 */
export function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Validate password strength
 */
export function validatePassword(password) {
  const errors = []
  
  if (!password) {
    errors.push('Password is required')
  } else {
    if (password.length < 6) {
      errors.push('Password must be at least 6 characters long')
    }
    if (password.length > 128) {
      errors.push('Password must be less than 128 characters')
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Validate OTP format
 */
export function validateOTP(otp) {
  const otpRegex = /^\d{6}$/
  return otpRegex.test(otp)
}

/**
 * Validate name format
 */
export function validateName(name) {
  const errors = []
  
  if (!name || !name.trim()) {
    errors.push('Name is required')
  } else {
    const trimmedName = name.trim()
    if (trimmedName.length < 2) {
      errors.push('Name must be at least 2 characters long')
    }
    if (trimmedName.length > 50) {
      errors.push('Name must be less than 50 characters')
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
}
